import * as yup from 'yup';

// iban.utils.ts
const ibanLengths: Record<string, number> = {
  DE: 22,
  AT: 20,
  CH: 21,
  FR: 27,
  NL: 18,
  IT: 27,
  GB: 22,
  ES: 24,
  BE: 16,
  PL: 28,
  HU: 28,
};

export const germanIbanExamples: string[] = [
  'DE44 5001 0517 5407 3249 31',
  'DE28 1000 0000 1234 5678 00',
  'DE19 2004 0000 0543 2103 00',
  'DE89 3704 0044 0532 0130 00',
  'DE91 3006 0601 0000 1234 56',
  'DE60 8605 5592 1234 5678 90',
  'DE13 8205 1000 0000 1000 04',
  'DE04 5121 0800 0000 1234 56',
  'DE66 6006 0400 0000 1234 56',
  'DE67 7002 0270 1234 5678 90',
];

export const internationalIbanExamples: string[] = [
  'HU42 1177 3016 1111 10180 0000 000', // HU
  'AT61 1904 3002 3457 3201', // AT
  'CH93 0076 2011 6238 5295 7', // CH
  'FR14 2004 1010 0505 0001 3M02 606', // FR
  'NL91 ABNA 0417 1643 00', // NL
  'IT60 X054 2811 1010 0000 0123 456', // IT
  'GB29 NWBK 6016 1331 9268 19', // GB
  'ES91 2100 0418 4502 0005 1332', // ES
  'BE68 5390 0754 7034 ', // BE
  'PL61 1090 1014 0000 0712 1981 2874', // PL
];

export const getMaxIbanLength = (iban: string | undefined | null): number => {
  const DEFAULT_IBAN_MAX = 34;
  if (!iban || iban.length < 2) return DEFAULT_IBAN_MAX;

  const countryCode = iban.slice(0, 2).toUpperCase();
  return ibanLengths[countryCode] ?? DEFAULT_IBAN_MAX;
};

export const getFormattedMaxIbanLength = (iban: string | undefined | null): number => {
  const rawLength = getMaxIbanLength(iban);
  const blockSize = 4;
  const numberOfBlocks = Math.ceil(rawLength / blockSize);
  const numberOfSpaces = numberOfBlocks - 1;
  return rawLength + numberOfSpaces;
};

/**
 * YUP VALIDATORS
 */

export const numberToNull = (value: unknown, originalValue: unknown) => {
  // Convert empty string or undefined to null
  return originalValue === '' || originalValue == null ? null : value;
};

export const nullableNumber = () => yup.number().nullable().transform(numberToNull);

// validationUtils.ts (weiter unten)

// this field >= siblingField
export const createGteSiblingTest = (siblingKey: string) =>
  function (this: yup.TestContext, value?: number | null) {
    const sibling = (this.parent as Record<string, unknown>)[siblingKey] as
      | number
      | null
      | undefined;

    // If one of them is missing, skip validation
    if (value == null || sibling == null) {
      return true;
    }

    // Current value must be >= sibling
    return value >= sibling;
  };

// this field <= siblingField
export const createLteSiblingTest = (siblingKey: string) =>
  function (this: yup.TestContext, value?: number | null) {
    const sibling = (this.parent as Record<string, unknown>)[siblingKey] as
      | number
      | null
      | undefined;

    // If one of them is missing, skip validation
    if (value == null || sibling == null) {
      return true;
    }

    // Current value must be <= sibling
    return value <= sibling;
  };

export const safeDateTransform = (value: unknown, originalValue: unknown) => {
  // Treat undefined, null, empty string and literal "undefined" as null
  if (
    originalValue === undefined ||
    originalValue === null ||
    originalValue === '' ||
    originalValue === 'undefined'
  ) {
    return null;
  }

  // If it's already a Date, keep it
  if (originalValue instanceof Date) {
    return originalValue;
  }

  // If it's a number or string, let yup try to parse it
  return value;
};

export const toValidDateOrNull = (input: unknown): Date | null => {
  if (!input) return null;
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  // handle string "undefined" etc.
  if (input === 'undefined') return null;

  const d = new Date(input as string);
  return isNaN(d.getTime()) ? null : d;
};

export const nullableDate = () =>
  yup
    .date()
    .transform((value, originalValue) => {
      // normalize nasty values
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue === '' ||
        originalValue === 'undefined'
      ) {
        return null;
      }

      if (originalValue instanceof Date) {
        return isNaN(originalValue.getTime()) ? null : originalValue;
      }

      const d = new Date(originalValue as string);
      return isNaN(d.getTime()) ? null : d;
    })
    .nullable();
